import { FC, useEffect, useState } from 'react';
import { Modal } from '@/components';
import { useForm, useController } from 'react-hook-form';
import {
  BoxV2 as Box,
  Flex,
  Button,
  TextInput,
  DropdownV2,
} from 'portal-commons';
import {
  isValidInput,
  createMaxLengthValidator,
  createRequiredValidator,
} from '@/utils/validator';
import { campaignController } from '@/apis/core';
import { CampaignDetail } from '@/apis/core/types';
import { RcsCarrierAction } from './types';
import { toastFlashMessage } from '@/utils';
import { SelectItem } from '@/types';

interface MnoStatusActionModalProps {
  open: boolean;
  action: RcsCarrierAction | null;
  campaign?: CampaignDetail;
  carrier: { mnoId: number; mno: string };
  onCancel: () => void;
  onComplete: (action: MnoStatusActionModalProps['action']) => void;
  dcaOptions?: { label: string; value: string; disabled?: boolean }[];
  suspensionCategoryOptions: SelectItem[];
}

const DIALOG_CONFIG: {
  [key in RcsCarrierAction]: { title: string; confirmButtonLabel: string };
} = {
  electDca: { title: 'Select DCA', confirmButtonLabel: 'Share' },
  updateDca: { title: 'Update DCA', confirmButtonLabel: 'Share' },
  activate: { title: 'Activate Campaign', confirmButtonLabel: 'Confirm' },
  suspend: { title: 'Suspend Campaign', confirmButtonLabel: 'Confirm' },
  unsuspend: { title: 'Activate Campaign', confirmButtonLabel: 'Confirm' },
  reject: { title: 'Reject Campaign', confirmButtonLabel: 'Confirm' },
  terminate: { title: 'Terminate Campaign', confirmButtonLabel: 'Confirm' },
};

const MnoStatusActionModal: FC<MnoStatusActionModalProps> = ({
  open,
  action,
  campaign,
  carrier,
  onCancel,
  dcaOptions,
  onComplete,
  suspensionCategoryOptions = [],
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const config = action ? DIALOG_CONFIG[action] : DIALOG_CONFIG.electDca;

  const { control, reset, handleSubmit } = useForm({
    mode: 'onTouched',
  });

  const onValidHandler = async (data: {
    explanation?: string;
    suspensionCategory?: string;
    dcaId?: string;
  }) => {
    if (!action || !campaign) return;
    setIsProcessing(true);
    if (['electDca', 'updateDca'].includes(action)) {
      const result = await campaignController.electSecondaryDca(
        {
          campaignUid: campaign?.uid,
          electedDcaUid: data.dcaId || '',
        },
        { mnoIds: [carrier.mnoId] }
      );
      if (result) {
        onComplete(action);
        toastFlashMessage('Secondary DCA elected successfully', 'success');
      }
    } else if (
      ['activate', 'reject', 'suspend', 'terminate', 'unsuspend'].includes(
        action
      )
    ) {
      const REMOTE_STATUS = {
        activate: 'ACTIVE',
        reject: 'REJECTED',
        suspend: 'SUSPENDED',
        terminate: 'TERMINATED',
        unsuspend: 'ACTIVE',
      };

      const result = await campaignController.updateCampaignOperationStatus(
        {
          campaignUid: campaign.uid,
          networkId: carrier.mnoId,
          status: REMOTE_STATUS[action],
        },
        {
          explanation: data.explanation || '',
          suspensionCategory: data.suspensionCategory,
        }
      );

      if (result) {
        onComplete(action);
        if (['activate', 'unsuspend'].includes(action)) {
          toastFlashMessage('Campaign activated successfully', 'success');
        } else if (action === 'reject') {
          toastFlashMessage('Campaign rejected successfully', 'success');
        } else if (action === 'suspend') {
          toastFlashMessage('Campaign suspended successfully', 'success');
        } else if (action === 'terminate') {
          toastFlashMessage('Campaign terminated successfully', 'success');
        }
      }
    }
    setIsProcessing(false);
  };

  useEffect(() => {
    if (!open) {
      reset();
      setIsProcessing(false);
    }
  }, [open]);

  return (
    <Modal open={open} testid="carrierStatusActionModal">
      <Box
        as="h3"
        sx={{
          py: 's',
          fontSize: 'H600',
          fontWeight: 600,
          lineHeight: 'normal',
          color: 't.black80',
          textAlign: 'center',
        }}
      >
        {config.title}
      </Box>
      <form noValidate onSubmit={handleSubmit(onValidHandler)}>
        {action && ['electDca', 'updateDca'].includes(action) ? (
          <ElectDcaDialogContent control={control} options={dcaOptions} />
        ) : (
          <StatusChangeDialogContent
            action={action}
            control={control}
            campaign={campaign}
            carrier={carrier}
            suspensionCategoryOptions={suspensionCategoryOptions}
          />
        )}
        <Flex sx={{ py: 30, gap: 18, justifyContent: 'center' }}>
          <Button
            variant="outline"
            data-testid="carrierStatusActionModalCancelButton"
            disabled={isProcessing}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isProcessing}
            data-testid="carrierStatusActionModalSubmitButton"
          >
            {config.confirmButtonLabel}
          </Button>
        </Flex>
      </form>
    </Modal>
  );
};

const StatusChangeDialogContent = ({
  control,
  campaign,
  carrier,
  action,
  suspensionCategoryOptions,
}) => {
  const isExplanationRequired = action !== 'activate';
  const isSuspensionCategoryVisible = action === 'suspend';
  const {
    field: {
      value: explanation,
      onChange: setExplanation,
      onBlur: validateExplanation,
    },
    fieldState: { error: explanationError },
  } = useController({
    control,
    name: 'explanation',
    rules: {
      validate: {
        ...(!isExplanationRequired
          ? {}
          : {
              isRequired: createRequiredValidator('Please enter explanation'),
            }),
        maxLength: createMaxLengthValidator(3000),
        pattern: (value) => isValidInput(value) || 'Invalid input',
      },
    },
  });

  const {
    field: {
      value: suspensionCategory,
      onChange: setSuspensionCategory,
      onBlur: validateSuspensionCategory,
    },
    fieldState: { error: suspensionCategoryError },
  } = useController({
    control,
    name: 'suspensionCategory',
    defaultValue: '',
    disabled: !isSuspensionCategoryVisible,
    rules: {
      validate: {
        isRequired: createRequiredValidator(
          'Please select Suspension Category'
        ),
      },
    },
  });

  return (
    <>
      <Flex sx={{ flexDirection: 'column', gap: 'm', mt: 15 }}>
        <Box
          as="span"
          sx={{
            fontSize: 'H300',
            fontWeight: 400,
            color: 't.black80',
          }}
        >
          * Indicates a Required Field
        </Box>
        <TextInput readOnly label="Campaign ID" value={campaign.uid || 'N/A'} />
        <Flex sx={{ gap: 's' }}>
          <TextInput
            readOnly
            label="Brand ID"
            value={campaign.brand.uid || 'N/A'}
            style={{ width: '124px' }}
          />
          <TextInput
            readOnly
            label="Brand DBA Name"
            value={campaign.brand.displayName || 'N/A'}
            style={{ flexGrow: 1 }}
          />
        </Flex>
        <Flex sx={{ gap: 's' }}>
          <TextInput
            readOnly
            label="CSP ID"
            value={campaign.brand.csp.uid || 'N/A'}
            style={{ width: '124px' }}
          />
          <TextInput
            readOnly
            label="CSP DBA Name"
            value={campaign.brand.csp.displayName || 'N/A'}
            style={{ flexGrow: 1 }}
          />
        </Flex>
        <TextInput readOnly label="Carrier" value={carrier.mno || '--'} />
        {isSuspensionCategoryVisible && (
          <DropdownV2
            {...suspensionCategory}
            label="Campaign Suspension Category"
            value={suspensionCategory}
            error={!!suspensionCategoryError?.message}
            helperText={suspensionCategoryError?.message}
            onChange={setSuspensionCategory}
            onBlur={validateSuspensionCategory}
            options={suspensionCategoryOptions}
            data-testid="suspensionCategory"
          />
        )}
        <TextInput
          required={isExplanationRequired}
          multiline
          maxRows={4}
          data-testid="explanationInput"
          label="Explanation"
          placeholder="Please enter Explanation"
          value={explanation}
          error={!!explanationError?.message}
          note={explanationError?.message}
          onChange={setExplanation}
          onBlur={validateExplanation}
        />
      </Flex>
    </>
  );
};

const ElectDcaDialogContent = ({ control, options }) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name: 'dcaId',
    control,
    defaultValue: '',
    rules: {
      required: 'This field is required',
    },
  });
  return (
    <Flex sx={{ flexDirection: 'column', gap: 'xs', my: 'xs' }}>
      <Box sx={{ pt: '7px', pb: '11px' }}>Select a DCA to share campaign.</Box>
      <DropdownV2
        {...field}
        required
        data-testid="selectDcaDropdown"
        options={options}
        label="Select DCA"
        error={!!error?.message}
        helperText={error?.message}
      />
    </Flex>
  );
};

export default MnoStatusActionModal;
